"use client";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import useToast from "@/contexts/toast-context";
import { CampaignFormData, ISegmentData } from "@/components/screens/CampaignFormPage/types";
import { normalizeSegmentData } from "@/components/templates/CampaignForm/form-data";
import IContact from "@/interfaces/contact.interface";
import { CampaignFormMessages } from "@/messages/campaigns/campaign-form.messages";
import campaignService from "@/services/campaign.service";
import { PaginatedResponse } from "@/types/requests.types";

import ContactsTable from "./Table";

type Props = {
  messages: CampaignFormMessages;
};

function ContactsSegment({ messages }: Props) {
  const { values } = useFormikContext<CampaignFormData>();
  const { showToast } = useToast();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [previewData, setPreviewData] = useState<PaginatedResponse<IContact> | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [lastSearchedSegmentKey, setLastSearchedSegmentKey] = useState<string | null>(null);

  const searchedSegmentRef = useRef<ISegmentData | null>(null);

  const normalizedCurrentSegment = useMemo(
    () => normalizeSegmentData(values.segment),
    [values.segment]
  );

  const currentSegmentKey = useMemo(
    () => JSON.stringify(normalizedCurrentSegment),
    [normalizedCurrentSegment]
  );

  const isButtonDisabled =
    hasSearched && lastSearchedSegmentKey === currentSegmentKey && !isSearching;

  const runSegmentSearch = useCallback(
    async (segmentPayload: ISegmentData) => {
      setIsSearching(true);

      try {
        const activePreview = await campaignService.getContacts(segmentPayload, {
          pageNumber,
          pageSize,
        });

        setPreviewData(activePreview);
        setHasSearched(true);
        setLastSearchedSegmentKey(currentSegmentKey);
        searchedSegmentRef.current = segmentPayload;
      } catch (error) {
        setPreviewData(null);
        setHasSearched(true);
        showToast({
          text: error instanceof Error ? error.message : messages.segmentSearchError,
          type: "error",
        });
      } finally {
        setIsSearching(false);
      }
    },
    [currentSegmentKey, messages.segmentSearchError, pageNumber, pageSize, showToast]
  );

  const handleSearch = () => {
    void runSegmentSearch(normalizedCurrentSegment);
  };

  useEffect(() => {
    if (!hasSearched || !searchedSegmentRef.current) return;
    if (lastSearchedSegmentKey !== currentSegmentKey) return;

    void runSegmentSearch(searchedSegmentRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize]);

  const pagination = {
    pageNumber,
    pageSize,
    changePage: (page: number) => setPageNumber(page + 1),
    extend: (newPageSize: number) => {
      setPageSize(newPageSize);
      setPageNumber(1);
    },
    next: () => setPageNumber((p) => p + 1),
    previous: () => setPageNumber((p) => Math.max(1, p - 1)),
  };

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.contactsSegmentTitle}
          </MDTypography>

          <MDButton
            variant="gradient"
            color="dark"
            onClick={handleSearch}
            isLoading={isSearching}
            disabled={isButtonDisabled}
          >
            {messages.search}
          </MDButton>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid size={12}>
              <ContactsTable
                contacts={previewData?.items || []}
                messages={messages}
                data={previewData}
                pagination={pagination}
                hasSearched={hasSearched && !isSearching}
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default ContactsSegment;
